import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem } from '@mui/material';
// routes
import { PATH_AUTH, PATH_LMS } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import { Role } from 'src/@types/lms/roles';

import API from 'src/api/api';
import APIPERSONAL from 'src/api/personal/apipersonal';
// import session from 'redux-persist/lib/storage/session';
import ChangeRoleDialog from './ChangeRoleDialog';

// ----------------------------------------------------------------------

const MENU_OPTIONS = [
  {
    label: 'Profile',
    linkTo: PATH_LMS.personal.profile,
  },
  // {
  //   label: 'Settings',
  //   linkTo: PATH_DASHBOARD.user.account,
  // },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuth();

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState<HTMLElement | null>(null);

  const [openRoles, setOpenRoles] = useState(false);

  const [roleData, setRoleData] = useState<Role[]>([
    {
      id: '',
      name: '',
      description: '',
      enabled: true,
    },
  ]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    //console.log(user);
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleOpenRoles = () => {
    setOpenRoles(true);
  };

  const handleCloseRoles = () => {
    setOpenRoles(false);
  };

  const onSubmitRole = async (data: Role) => {
    submitLogin(data.name);
  };

  async function submitLogin(role: string) {
    try {
      let api = new API();
      let dataResp = await api.changeRole(role);
      if (dataResp.success) {
        window.location.href = PATH_LMS.inspections.list;
      } else {
        enqueueSnackbar(dataResp.errorMessage, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    }
  }

  const handleLogout = async () => {
    try {
      let api = new API();
      //console.log('loging out')
      await api.logout();
      await logout();
      navigate(PATH_AUTH.login, { replace: true });
      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const handleChangeRole = async () => {
    let apipersonal = new APIPERSONAL();
    apipersonal.init();
    let resp = await apipersonal.getUserRoles();
    if (resp.length === 0) {
      enqueueSnackbar('You have no roles assigned, please contact your administrator', {
        variant: 'error',
      });
    } else if (resp.length === 1) {
      enqueueSnackbar('You have only one role assigned', { variant: 'error' });
    } else {
      setRoleData(resp.filter((obj) => obj.name !== user?.role));
      handleOpenRoles();
    }
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {user?.userName}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              to={option.linkTo}
              component={RouterLink}
              onClick={handleClose}
            >
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />
        <MenuItem onClick={handleChangeRole} sx={{ m: 1 }}>
          Change Active Role
        </MenuItem>
        <Divider sx={{ borderStyle: 'dashed' }} />
        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
        <ChangeRoleDialog
          values={roleData}
          isOpen={openRoles}
          onClose={handleCloseRoles}
          onSubmitRole={onSubmitRole}
        />
      </MenuPopover>
    </>
  );
}
